import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { MessageCircle, Send, X, MoreHorizontal, Trash2, Pencil, Image as ImageIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { FlagImage } from './utils/countryFlags';
import { PostMenu } from './ui/PostMenu';
import { AnonymousAvatar, ANONYMOUS_USER } from './AnonymousAvatar';
import { LegionAvatarWrapper } from './LegionAvatarWrapper';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { useYapInteractions } from './YapInteractionsContext';
import { formatTimeAgo } from './utils/timeFormat';
import { YapService } from '../services/api';
import { StorageService } from '../services/StorageService';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { VoiceRecorderUI } from './VoiceRecorderUI';
import { VoiceReplyBubble } from './VoiceReplyBubble';
import { LikeButton } from './ui/LikeButton';
import { ImageWithPreview } from './ImageWithPreview';
import { FriendBadge } from './ui/FriendBadge';
import { LikersModal } from './ui/LikersModal';
import { FormattedText } from './FormattedText';
import { extractFirstUrl } from './utils/textParsers';
import { LinkPreview } from './LinkPreview';
import RankBadge from './RankBadge';
import { EditPostModal } from './EditPostModal';

/**
 * The reply box and threaded reply list for a post's detail screen.
 *
 * Shared by YapDetailScreen and ShoutDetailsScreen. Shout Details used to carry
 * its own copy of all of this, and it fell behind: no branch replies, no
 * collapsing, no editing, no cascade on delete. One component means a reply
 * feature added to yaps reaches shouts without anyone having to remember.
 */

export interface Reply {
  id: string;
  authorId: string | number;
  author: string;
  username: string;
  avatar: string;
  content: string;
  time: string;
  likes: number;
  likedBy: (string | number)[];
  authorCountryFlag?: string;
  authorRank?: string;
  type?: 'text' | 'voice';
  audioUrl?: string;
  duration?: number;
  parentReplyId?: string;
  image?: string;
}

interface PostRepliesProps {
  /** The document the replies live under (its `comments` subcollection). */
  postId: string;
  /** Where the post lives, when known. Skips the lookup when editing a reply. */
  collectionName?: string;
  /** The id and type the on-screen reply counter is keyed to. For a simple
   *  repost this is the ORIGINAL yap, not postId. */
  countId: string;
  countType: 'yap' | 'shout';
  /** Author shown as "Original Poster" on their own replies. */
  opAuthorId?: string | number | null;
  /** Owner of the post, who may delete any reply under it. */
  postOwnerId?: string | number | null;
  isAnonymousPost?: boolean;
  /** Group membership gate. False disables replying and liking. */
  canInteract: boolean;
  /** Tribe posts take no replies at all. */
  repliesDisabled?: boolean;
  /** The reply box is opened from the post's own action row, so the parent owns it. */
  showReplyBox: boolean;
  onShowReplyBoxChange: (open: boolean) => void;
  /** Called after a reply is posted or deleted. */
  onRepliesChanged?: () => void;
}

export function PostReplies({
  postId, collectionName, countId, countType, opAuthorId, postOwnerId, isAnonymousPost = false,
  canInteract, repliesDisabled = false, showReplyBox, onShowReplyBoxChange, onRepliesChanged,
}: PostRepliesProps) {
  const { isRecording, isPaused, duration, startRecording, stopRecording, pauseRecording, resumeRecording, audioBlob, reset } = useVoiceRecorder(10000);
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const [voicePreviewUrl, setVoicePreviewUrl] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  // Optional image attached to a reply.
  const [replyImageFile, setReplyImageFile] = useState<File | null>(null);
  const [replyImagePreview, setReplyImagePreview] = useState<string | null>(null);
  const replyImageInputRef = useRef<HTMLInputElement>(null);
  // Threaded replies: when set, the next submit becomes a branch reply under the given parent.
  const [replyingTo, setReplyingTo] = useState<{ id: string; username: string } | null>(null);
  // Set of reply IDs whose child branches are hidden by the user.
  const [collapsedThreads, setCollapsedThreads] = useState<Set<string>>(new Set());
  // Branch roots (at the visual-depth cap) whose flattened sub-thread is expanded inline.
  const [expandedDeepThreads, setExpandedDeepThreads] = useState<Set<string>>(new Set());
  const replyBoxRef = useRef<HTMLDivElement>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [inProgressDeleteIds, setInProgressDeleteIds] = useState<Set<string>>(new Set());
  const [editingReply, setEditingReply] = useState<any>(null);
  const [likersReply, setLikersReply] = useState<Reply | null>(null);

  const { userProfile, isFriend } = useUserData();
  const { isBlockedContent } = useContentFilter();
  const { adjustReplyCount } = useYapInteractions();

  // 🎙️ VOICE REPLY: Generate preview when recording stops
  useEffect(() => {
    if (!audioBlob || isRecording) return;
    const url = URL.createObjectURL(audioBlob);
    setVoicePreviewUrl(url);
    setVoiceBlob(audioBlob);
  }, [audioBlob, isRecording]);

  // Clean up object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (voicePreviewUrl) URL.revokeObjectURL(voicePreviewUrl);
    };
  }, [voicePreviewUrl]);

  useEffect(() => {
    return () => { if (replyImagePreview) URL.revokeObjectURL(replyImagePreview); };
  }, [replyImagePreview]);

  const handleReplyImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    if (replyImagePreview) URL.revokeObjectURL(replyImagePreview);
    setReplyImageFile(file);
    setReplyImagePreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const clearReplyImage = () => {
    if (replyImagePreview) URL.revokeObjectURL(replyImagePreview);
    setReplyImageFile(null);
    setReplyImagePreview(null);
  };

  const fetchReplies = async () => {
    if (!postId) return;
    try {
      const fetchedReplies = await YapService.fetchYapReplies(postId);
      const mappedReplies = fetchedReplies.map((r: any) => ({
        ...r,
        time: r.timestamp?.toDate ? formatTimeAgo(r.timestamp.toDate()) : 'Recently',
        _ts: r.timestamp?.toDate ? r.timestamp.toDate().getTime() : (r.timestamp?.seconds ? r.timestamp.seconds * 1000 : 0)
      }));
      // Sort oldest → newest
      mappedReplies.sort((a: any, b: any) => a._ts - b._ts);
      setReplies(mappedReplies);
    } catch (error) {
      console.error('[PostReplies] Error fetching replies:', error);
    }
  };

  useEffect(() => {
    setReplies([]);
    void fetchReplies();
  }, [postId]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeReplyBox = () => {
    onShowReplyBoxChange(false);
    if (voicePreviewUrl) URL.revokeObjectURL(voicePreviewUrl);
    setVoicePreviewUrl(null);
    setVoiceBlob(null);
    clearReplyImage();
    reset();
  };

  const handlePostReply = async () => {
    if (isRecording) {
      stopRecording();
      return;
    }

    const hasContent = replyText.trim() || voiceBlob || replyImageFile;
    if (!hasContent) return;

    if ((userProfile as any)?.status === 'banned') {
      toast.error('Your account has been suspended. You cannot post replies.');
      return;
    }

    const loadingToast = toast.loading('Posting reply...');
    try {
      const userIdForStorage = userProfile?.id;
      let audioUrl = '';
      if (voiceBlob) {
        if (!userIdForStorage) throw new Error("Authentication required");
        audioUrl = await StorageService.uploadVoiceReply(String(userIdForStorage), voiceBlob);
      }

      let imageUrl = '';
      if (replyImageFile) {
        if (!userIdForStorage) throw new Error("Authentication required");
        imageUrl = await StorageService.uploadReplyImage(String(userIdForStorage), replyImageFile);
      }

      const newReply: Reply = {
        id: `reply_${Date.now()}`,
        authorId: userProfile?.id || '1',
        author: userProfile?.name || 'User',
        username: `@${(userProfile?.username || 'user').replace(/^@+/, '')}`,
        avatar: (userProfile?.portfolio && userProfile.portfolio[userProfile.profilePictureIndex || 0]) || '',
        authorCountryFlag: userProfile?.countryFlag || userProfile?.country || '',
        ...(userProfile?.rank ? { authorRank: userProfile.rank } : {}),
        content: replyText.trim() || (voiceBlob ? '[Voice Reply]' : ''),
        type: voiceBlob ? 'voice' : 'text',
        ...(audioUrl ? { audioUrl } : {}),
        ...(voiceBlob ? { duration } : {}),
        ...(imageUrl ? { image: imageUrl } : {}),
        time: 'Now',
        likes: 0,
        likedBy: [],
        ...(replyingTo?.id ? { parentReplyId: replyingTo.id } : {}),
      };

      const realId = await YapService.addReply(postId, newReply);
      // timestamp so the Edit option shows on a reply posted this session.
      setReplies(prev => [...prev, { ...newReply, id: realId, timestamp: new Date().toISOString() } as any]);
      adjustReplyCount(countId, 1, countType);

      setReplyText('');
      setVoiceBlob(null);
      if (voicePreviewUrl) URL.revokeObjectURL(voicePreviewUrl);
      setVoicePreviewUrl(null);
      clearReplyImage();
      onShowReplyBoxChange(false);
      setReplyingTo(null);
      toast.dismiss(loadingToast);
      toast.success('Reply posted!');
      onRepliesChanged?.();
    } catch (e: any) {
      toast.dismiss(loadingToast);
      toast.error(e.message || "Failed to post reply. Please try again.");
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    if (inProgressDeleteIds.has(replyId)) return;

    setInProgressDeleteIds(prev => new Set(prev).add(replyId));
    try {
      await YapService.deleteReply(postId, replyId);

      // Cascade: also remove every descendant locally so the UI matches the
      // service-side cascade. Walk the tree before filtering.
      const removed = new Set<string>([replyId]);
      let frontier: string[] = [replyId];
      while (frontier.length > 0) {
        const next: string[] = [];
        for (const parentId of frontier) {
          for (const r of replies) {
            if (r.parentReplyId === parentId && !removed.has(r.id)) {
              removed.add(r.id);
              next.push(r.id);
            }
          }
        }
        frontier = next;
      }
      const deletedCount = removed.size;
      setReplies(prev => prev.filter(r => !removed.has(r.id)));

      toast.success(deletedCount > 1 ? `Deleted reply and ${deletedCount - 1} follow-up${deletedCount - 1 === 1 ? '' : 's'}` : 'Reply deleted');

      // The service decrements once per removed doc.
      for (let i = 0; i < deletedCount; i++) adjustReplyCount(countId, -1, countType);

      window.dispatchEvent(new CustomEvent('yapUpdated'));
      onRepliesChanged?.();
    } catch (e: any) {
      console.error("[PostReplies] Error deleting reply:", e);
      toast.error(e.message || "Failed to delete reply");
    } finally {
      setInProgressDeleteIds(prev => {
        const next = new Set(prev);
        next.delete(replyId);
        return next;
      });
    }
  };

  // Start a branch reply: prefill @username, open the input, scroll it into view.
  const handleReplyToReply = (reply: Reply) => {
    const cleanHandle = (reply.username || '').replace(/^@+/, '');
    setReplyingTo({ id: reply.id, username: cleanHandle });
    onShowReplyBoxChange(true);
    setReplyText(prev => {
      const mention = `@${cleanHandle} `;
      if (prev.trim().length === 0) return mention;
      return prev.includes(mention.trim()) ? prev : `${mention}${prev}`;
    });
    setTimeout(() => {
      replyBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const toggleThreadCollapse = (replyId: string) => {
    setCollapsedThreads(prev => {
      const next = new Set(prev);
      if (next.has(replyId)) next.delete(replyId);
      else next.add(replyId);
      return next;
    });
  };

  const toggleDeepThread = (replyId: string) => {
    setExpandedDeepThreads(prev => {
      const next = new Set(prev);
      if (next.has(replyId)) next.delete(replyId);
      else next.add(replyId);
      return next;
    });
  };

  const handleToggleReplyLike = async (reply: Reply, isLiked: boolean) => {
    if (!canInteract) {
      toast.error("Members only");
      return;
    }

    const currentUserId = String(userProfile?.id || '1');
    const isLikedNow = !isLiked;

    const applyLike = (liked: boolean) => setReplies(prev => prev.map(r => {
      if (r.id !== reply.id) return r;
      return {
        ...r,
        likes: liked ? r.likes + 1 : Math.max(0, r.likes - 1),
        likedBy: liked
          ? [...(r.likedBy || []), currentUserId]
          : (r.likedBy || []).filter(id => String(id) !== currentUserId)
      };
    }));

    applyLike(isLikedNow);

    try {
      if (isLikedNow) {
        await YapService.likeReply(postId, reply.id);
      } else {
        await YapService.unlikeReply(postId, reply.id);
      }
      await fetchReplies();
    } catch (err: any) {
      console.error("[PostReplies] Error toggling reply like:", err);
      toast.error("Failed to update like. Please try again.");
      applyLike(!isLikedNow);
    }
  };

  const currentUserId = String(userProfile?.id || '1');
  const isPostOwner = !!postOwnerId && String(postOwnerId) === currentUserId;

  const renderCard = (reply: Reply) => {
    const isLiked = reply.likedBy?.some(id => String(id) === currentUserId);
    const isAuthorOp = !!opAuthorId && String(reply.authorId) === String(opAuthorId);
    const replyAuthorId = typeof reply.authorId === 'object' ? (reply.authorId as any)?.id : reply.authorId;
    const isFriendReplyAuthor = !(isAnonymousPost && isAuthorOp) && !!replyAuthorId && String(replyAuthorId) !== currentUserId && isFriend(String(replyAuthorId));
    const isOwnReply = String(replyAuthorId) === currentUserId;

    return (
      <motion.div
        key={reply.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 rounded-2xl bg-white/60 dark:bg-white/5 border border-black/10 dark:border-white/10 relative w-full max-w-full overflow-hidden"
      >
        <div className="flex items-start gap-3">
          {isAnonymousPost && isAuthorOp ? (
            <AnonymousAvatar className="w-10 h-10" size="sm" />
          ) : (
            <LegionAvatarWrapper rank={typeof reply.authorId === 'object' ? (reply.authorId as any).rank : undefined} className="shadow-sm">
              <Avatar
                className="w-12 h-12 border-2 border-[#4478ea] dark:border-[#4478ea] cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  if (reply.username === userProfile?.username) {
                    window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'profile' }));
                  } else {
                    window.dispatchEvent(new CustomEvent('navigateToFriendProfile', {
                      detail: {
                        id: reply.authorId,
                        name: reply.author,
                        username: reply.username,
                        image: reply.avatar
                      }
                    }));
                  }
                }}
              >
                <AvatarImage src={reply.avatar} />
                <AvatarFallback>{reply.author?.[0]}</AvatarFallback>
              </Avatar>
            </LegionAvatarWrapper>
          )}
          <div className="flex-1 min-w-0 pr-2 flex flex-col pt-0.5">
            {/* Reply Header: Name + Flag */}
            <div className="flex items-center gap-1.5 min-h-[20px]">
              <h5 className="flex items-center gap-1.5 text-sm font-bold text-black dark:text-white truncate">
                <span>{isAnonymousPost && isAuthorOp ? 'Anonymous' : reply.author}</span>
                {!(isAnonymousPost && isAuthorOp) && reply.authorRank && (
                  <RankBadge rank={reply.authorRank} size={18} />
                )}
              </h5>
              {!(isAnonymousPost && isAuthorOp) && reply.authorCountryFlag && (
                <>
                  <span className="text-[11px] opacity-40 shrink-0">&bull;</span>
                  <FlagImage
                    countryName={reply.authorCountryFlag}
                    size="w80"
                    className="w-3.5 h-2.5 rounded shrink-0"
                  />
                </>
              )}
              {isFriendReplyAuthor && <FriendBadge className="shrink-0" />}
            </div>

            {/* Reply Metadata: @username • Time */}
            <div className="flex items-center gap-1.5 text-xs text-[#8e8e8e] dark:text-[#a0a0a0] mt-0.5 mb-2">
              <span className="truncate">
                {isAnonymousPost && isAuthorOp ? ANONYMOUS_USER.username : `@${(reply.username || 'user').replace(/^@+/, '')}`}
              </span>
              <span className="text-[8px] opacity-40 shrink-0">•</span>
              <span className="whitespace-nowrap shrink-0">{reply.time}</span>
              {/* An edit that leaves no trace lets a reply be
                  rewritten under the answers to it. */}
              {(reply as any).edited && (
                <span className="whitespace-nowrap shrink-0 opacity-60">· edited</span>
              )}
              {isAuthorOp && (
                <>
                  <span className="text-[8px] opacity-40 shrink-0">•</span>
                  <span className="whitespace-nowrap shrink-0 font-medium" style={{ color: '#4478ea' }}>Original Poster</span>
                </>
              )}
            </div>

            <div className="flex-1 min-w-0 space-y-2">
              {reply.content && reply.content !== '[Voice Reply]' && (
                <>
                  <FormattedText
                    text={reply.content}
                    className="text-sm text-[#373737] dark:text-[#E5E5E5] leading-relaxed wrap-break-word block font-medium"
                  />
                  {extractFirstUrl(reply.content) && (
                    <LinkPreview url={extractFirstUrl(reply.content)!} />
                  )}
                </>
              )}

              {/* Older shout replies were written without type: 'voice', so the
                  audio URL alone decides. */}
              {reply.audioUrl && (
                <VoiceReplyBubble
                  audioUrl={reply.audioUrl}
                  duration={reply.duration}
                  className="mb-1"
                />
              )}

              {reply.image && (
                <div className="mt-1 rounded-xl overflow-hidden w-fit max-w-full">
                  <ImageWithPreview
                    src={reply.image}
                    alt="Reply image"
                    className="max-h-72 w-auto object-cover rounded-xl"
                  />
                </div>
              )}
            </div>

            {/* Reply Actions: Like + Reply */}
            <div className="flex items-center gap-1">
              <LikeButton
                postId={reply.id}
                postType={countType} // placeholder, unused with overrides
                originalLikeCount={reply.likes}
                isLikedOverride={isLiked}
                displayCountOverride={reply.likes}
                className={`self-start px-2 py-1 ${isLiked ? 'text-[#E74C3C]' : 'text-[#999999] dark:text-[#E5E5E5]'}`}
                onToggleOverride={() => handleToggleReplyLike(reply, !!isLiked)}
              />

              {/* Reply (branch) button */}
              {canInteract && !repliesDisabled && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleReplyToReply(reply)}
                  className="h-auto self-start px-2 py-1 text-sm flex items-center gap-1.5 text-[#999999] dark:text-[#E5E5E5] hover:text-[#4478ea]"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Reply</span>
                </Button>
              )}
            </div>
          </div>

          {/* Top-right cluster: See Likes (own reply) + Delete menu */}
          {(isOwnReply || isPostOwner) && (
            <div className="absolute top-2 right-2 flex items-center gap-1">
              {isOwnReply && reply.likes > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLikersReply(reply)}
                  className="h-8 px-2 text-xs font-medium text-[#999999] dark:text-[#a0a0a0] hover:text-[#E74C3C]"
                >
                  See Likes
                </Button>
              )}
              {/* Shared menu. Replies gain Report, Block and View Profile —
                  report and block belong on every piece of content by another
                  person, and the hand-written menu here offered neither.

                  viewerOwnsParent carries the rule this menu already had: the
                  POST's author can delete any reply on it, while only the
                  reply's own author may edit. */}
              <PostMenu
                post={{ ...(reply as any), id: String(reply.id) }}
                surface="reply"
                collectionName={collectionName}
                viewerOwnsParent={isPostOwner}
                onEditRequested={() => setEditingReply(reply)}
                onDeleteRequested={() => handleDeleteReply(reply.id)}
              />
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  // ── Threaded replies ────────────────────────────────────────
  // Build a parent→children map so branch replies (with parentReplyId)
  // render indented under their parent with a connecting vertical line.
  const childrenByParent = new Map<string, Reply[]>();
  const topLevel: Reply[] = [];
  // 🚫 Blocked users: drop their replies (and, with them, any
  // branch hanging off a blocked reply, since its parent is gone).
  for (const r of replies.filter((r: any) => !isBlockedContent(r))) {
    if (r.parentReplyId) {
      const arr = childrenByParent.get(r.parentReplyId) || [];
      arr.push(r);
      childrenByParent.set(r.parentReplyId, arr);
    } else {
      topLevel.push(r);
    }
  }

  // Max nesting levels that still get their own left-indent + connectors.
  // Deeper than this, a branch is flattened (no further indent/connectors)
  // behind a "N more replies in this thread" toggle so bubbles never
  // overflow the right edge on narrow screens (max indent = 3 * 36 = 108px).
  const MAX_VISUAL_DEPTH = 3;

  // All descendants of a reply, DFS pre-order (parent before its children),
  // so the flattened list preserves conversational flow.
  const collectDescendants = (rootId: string): Reply[] => {
    const out: Reply[] = [];
    const walk = (id: string) => {
      for (const c of childrenByParent.get(id) || []) {
        out.push(c);
        walk(c.id);
      }
    };
    walk(rootId);
    return out;
  };

  const renderBranch = (reply: Reply, depth: number): React.ReactElement => {
    const children = childrenByParent.get(reply.id) || [];
    const isCollapsed = collapsedThreads.has(reply.id);
    const hasChildren = children.length > 0;

    // At the visual-depth cap: stop nesting. Flatten every descendant of
    // this branch (no further indent/connectors) behind a toggle row that
    // reflects only THIS sub-thread's remaining reply count.
    if (depth >= MAX_VISUAL_DEPTH && hasChildren) {
      const descendants = collectDescendants(reply.id);
      const isExpanded = expandedDeepThreads.has(reply.id);
      return (
        <div key={reply.id}>
          {renderCard(reply)}
          <div className="pt-2">
            <button
              type="button"
              onClick={() => toggleDeepThread(reply.id)}
              aria-expanded={isExpanded}
              className="flex items-center gap-1.5 px-2 py-1 text-xs font-semibold text-[#4478ea] hover:underline"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 shrink-0" />
                  <span>Hide replies</span>
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 shrink-0" />
                  <span>
                    {descendants.length} more {descendants.length === 1 ? 'reply' : 'replies'} in this thread
                  </span>
                </>
              )}
            </button>
            {isExpanded && (
              <div className="flex flex-col gap-4 pt-2">
                {descendants.map(d => (
                  <div key={d.id}>{renderCard(d)}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={reply.id}>
        {renderCard(reply)}
        {hasChildren && !isCollapsed && (
          <div className="relative pt-2">
            <div className="flex flex-col">
              {children.map((c, idx) => {
                const isLast = idx === children.length - 1;
                return (
                  <div key={c.id} style={{ position: 'relative', paddingLeft: '36px', paddingBottom: isLast ? 0 : '12px' }}>
                    {/* Vertical segment for THIS child only.
                        Non-last: spans the full wrapper height so it visually
                        joins to the next sibling's segment.
                        Last: stops at the horizontal connector → clean L-shape. */}
                    <button
                      type="button"
                      onClick={() => toggleThreadCollapse(reply.id)}
                      aria-label="Collapse thread"
                      title="Click to collapse thread"
                      style={{
                        position: 'absolute',
                        left: '18px',
                        top: 0,
                        height: isLast ? '33px' : '100%',
                        width: '3px',
                        background: '#4478ea',
                        borderRadius: '999px',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        opacity: 0.5,
                        transition: 'opacity 0.15s',
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.5'; }}
                    />
                    {/* Horizontal connector from vertical line to this child's card. */}
                    <div
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left: '18px',
                        top: '30px',
                        width: '18px',
                        height: '3px',
                        background: '#4478ea',
                        borderRadius: '999px',
                        opacity: 0.5,
                      }}
                    />
                    {renderBranch(c, depth + 1)}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {hasChildren && isCollapsed && (
          <button
            type="button"
            onClick={() => toggleThreadCollapse(reply.id)}
            className="ml-7 mt-2 text-xs font-semibold text-[#4478ea] hover:underline"
          >
            ↳ Show {children.length} {children.length === 1 ? 'reply' : 'replies'}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Reply Box */}
      {showReplyBox && !repliesDisabled && (
        <motion.div
          ref={replyBoxRef}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 rounded-2xl bg-white/60 dark:bg-white/5 border border-black/10 dark:border-white/10"
        >
          {/* Branch reply indicator */}
          {replyingTo && (
            <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-[#4478ea]/10 border border-[#4478ea]/20">
              <MessageCircle className="w-3.5 h-3.5 text-[#4478ea] shrink-0" />
              <span className="text-xs text-[#373737] dark:text-[#E5E5E5]">
                Replying to <span className="font-semibold text-[#4478ea]">@{replyingTo.username}</span>
              </span>
              <button
                type="button"
                onClick={() => setReplyingTo(null)}
                className="ml-auto text-[#999999] hover:text-[#373737] dark:hover:text-white"
                aria-label="Cancel branch reply"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <Textarea
            placeholder={voicePreviewUrl ? "Add a caption to your voice reply..." : "Write a reply..."}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            className="mb-3 bg-[#e7d299] dark:bg-black/30 border-black/10 dark:border-white/20 text-black dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5] resize-none"
            rows={3}
          />

          <div className="flex flex-col gap-3">
            {/* 🎙️ VOICE PREVIEW PLAYER */}
            {voicePreviewUrl && (
              <div className="mb-1 relative group w-fit">
                <VoiceReplyBubble
                  audioUrl={voicePreviewUrl}
                  className="pr-10"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    URL.revokeObjectURL(voicePreviewUrl);
                    setVoicePreviewUrl(null);
                    setVoiceBlob(null);
                    reset();
                  }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-md z-10"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* 🖼️ IMAGE PREVIEW */}
            {replyImagePreview && (
              <div className="mb-1 relative w-fit">
                <img
                  src={replyImagePreview}
                  alt="Reply attachment preview"
                  className="max-h-40 rounded-xl border border-black/10 dark:border-white/10 object-cover"
                />
                <button
                  onClick={(e) => { e.stopPropagation(); clearReplyImage(); }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-md z-10"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* 🎙️ Recording Bar: Takes full width above buttons when active */}
            {!voicePreviewUrl && isRecording && (
              <div className="mb-1">
                <VoiceRecorderUI
                  isRecording={isRecording}
                  isPaused={isPaused}
                  duration={duration}
                  maxDuration={10000}
                  onStart={startRecording}
                  onStop={stopRecording}
                  onCancel={reset}
                  onPause={pauseRecording}
                  onResume={resumeRecording}
                />
              </div>
            )}

            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {!voicePreviewUrl && !isRecording && (
                  <VoiceRecorderUI
                    isRecording={isRecording}
                    isPaused={isPaused}
                    duration={duration}
                    maxDuration={10000}
                    onStart={startRecording}
                    onStop={stopRecording}
                    onCancel={reset}
                    onPause={pauseRecording}
                    onResume={resumeRecording}
                  />
                )}
                {/* 🖼️ Attach image (hidden when recording/voice preview) */}
                {!voicePreviewUrl && !isRecording && (
                  <>
                    <input
                      ref={replyImageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleReplyImageSelect}
                    />
                    <button
                      type="button"
                      onClick={() => replyImageInputRef.current?.click()}
                      disabled={!!replyImagePreview}
                      aria-label="Attach image"
                      className="w-9 h-9 rounded-full flex items-center justify-center text-[#4478ea] bg-[#4478ea]/10 hover:bg-[#4478ea]/20 transition-colors disabled:opacity-40"
                    >
                      <ImageIcon className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              <div className="flex justify-end gap-2 shrink-0">
                <Button
                  variant="outline"
                  onClick={closeReplyBox}
                  className="bg-transparent border-black/20 dark:border-white/20 text-[#373737] dark:text-[#E5E5E5] hover:bg-black/5 dark:hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  variant="ghost"
                  onClick={handlePostReply}
                  disabled={(!replyText.trim() && !voiceBlob && !replyImageFile && !isRecording)}
                  className="bg-brand-gradient text-white shadow-lg disabled:opacity-40 px-6 py-2 rounded-xl font-bold flex items-center justify-center transition-all active:scale-95 border border-white/10"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isRecording ? 'Stop' : (voiceBlob ? 'Post Voice' : 'Reply')}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Replies Section */}
      <div className="space-y-4">
        <h3 className="text-xl text-black dark:text-white mb-4">Replies</h3>

        {replies.length === 0 ? (
          <div className="p-8 text-center">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 text-[#999999] dark:text-[#666666]" />
            <p className="text-[#373737] dark:text-[#999999] mb-2">No replies yet</p>
            {!repliesDisabled && canInteract && (
              <button
                onClick={() => onShowReplyBoxChange(true)}
                className="text-[#F39C12] dark:text-[#4478ea] hover:underline"
              >
                Leave the first reply
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {topLevel.map(r => renderBranch(r, 0))}
          </div>
        )}
      </div>

      <LikersModal
        open={!!likersReply}
        onClose={() => setLikersReply(null)}
        userIds={(likersReply?.likedBy || []).map(String)}
        title="Liked your reply"
      />
      <EditPostModal
        open={!!editingReply}
        onClose={() => setEditingReply(null)}
        postId={postId}
        collectionName={collectionName}
        replyId={String(editingReply?.id || '')}
        initialContent={String(editingReply?.content || '')}
        timestamp={(editingReply as any)?.timestamp}
        onSaved={(next) => setReplies((prev: any[]) => prev.map((r: any) =>
          String(r.id) === String(editingReply?.id) ? { ...r, content: next, edited: true } : r))}
      />
    </>
  );
}
